import { rememberedSharedScope, scopeDatabaseName, sharedScope, type SharedScope } from "../data/scopes.js";
import { openSharedStore, type SharedStore } from "../data/store.js";
import { switchableStore, type SwitchableStore } from "../data/switchable.js";
import { rememberedUser, updateDisplayName } from "./auth.js";
import { ApiError } from "./http.js";
import { addToMyLists, holdAddForSignIn, resolveShare, setGuestName, type Listed, type ResolvedShare, type ShareCredential, type ShareVisitor } from "./sharing.js";

/**
 * One visit to a shared list through one link (g72): what the page shows
 * before and after the link is known, the store it reads, and the visitor's
 * name.
 *
 * Beside the facades rather than among the screens: it asks the server who is
 * looking (`sharing.ts`), names an account (`auth.ts`) and opens the scope's
 * store, and no screen does any of those. The link is held here, in memory,
 * and in the page's own address; it goes nowhere else.
 */

export type VisitState =
  /** Asking the server, with no copy on this device to show meanwhile. */
  | { kind: "loading" }
  /** The link opens nothing: malformed, turned off, replaced, or its list is gone. */
  | { kind: "unavailable" }
  /** No connection, and no copy on this device yet. */
  | { kind: "unreachable" }
  /** Refused for too many attempts from this network. */
  | { kind: "limited" }
  /** An added list opened signed out (g132): only the account that added it can open it. */
  | { kind: "signin" }
  /** This browser will not store anything. */
  | { kind: "nostorage" }
  | { kind: "open"; store: SwitchableStore };

export interface SharedVisit {
  state(): VisitState;
  onState(fn: (state: VisitState) => void): () => void;
  /** Who the server last said is looking, or undefined until it has. */
  visitor(): ShareVisitor | undefined;
  /** Whether the list is in the visitor's Lists, as the server last said (g132). */
  listed(): Listed | undefined;
  onVisitor(fn: (visitor: ShareVisitor) => void): () => void;
  /** The scope the page reads now. */
  scope(): SharedScope | undefined;
  /** Asks again, after a failure. */
  retry(): void;
  /** Saves the visitor's name; see `NamePort.name`. */
  name(displayName: string): Promise<void>;
  /** Asks the server again who is looking, as after a sign-in in another tab. */
  refresh(): Promise<void>;
  /**
   * Adds the list to the signed-in visitor's Lists, by its link (g132). Rejects
   * as the server does: `sign_in_required`, `own_list`, `added_lists_full`.
   */
  add(): Promise<void>;
  /** Holds a guest's add for the sign-in they are about to make (g132). */
  holdAdd(): Promise<void>;
  close(): Promise<void>;
}

export interface VisitDeps {
  resolve: typeof resolveShare;
  nameGuest: typeof setGuestName;
  nameAccount: typeof updateDisplayName;
  add: typeof addToMyLists;
  holdAdd: typeof holdAddForSignIn;
  open: (scope: SharedScope) => Promise<SharedStore>;
  /** The account this browser is signed in to, if any; by default the one it remembers. */
  account?: () => string | undefined;
}

// Each read when called, not when this module loads: a test may replace the
// module one of them comes from without the page ever opening a link.
const defaults: VisitDeps = {
  resolve: (token) => resolveShare(token),
  nameGuest: (token, name) => setGuestName(token, name),
  nameAccount: (name) => updateDisplayName(name),
  add: (token) => addToMyLists(token),
  holdAdd: (token) => holdAddForSignIn(token),
  open: (scope) => openSharedStore(scope),
};

export function startSharedVisit(credential: ShareCredential, deps: VisitDeps = defaults): SharedVisit {
  let state: VisitState = { kind: "loading" };
  const account = (): string | undefined => (deps.account ? deps.account() : rememberedUser()?.id);
  let visitor: ShareVisitor | undefined;
  let resolved: ResolvedShare | undefined;
  let scope: SharedScope | undefined;
  let page: SwitchableStore | undefined;
  /** Added on this visit, before any answer from the server says so (g132). */
  let addedNow = false;
  let closed = false;
  /** Bumped by every resolve, so a late answer to an older one is dropped. */
  let asked = 0;
  /**
   * The server's answer and the move to its scope, while either is under way.
   * Naming waits for both: a name saved while the page still reads another
   * visitor's copy would sync that copy, be held as someone else, and drop
   * the change it was asked for.
   */
  let settling: Promise<void> = Promise.resolve();
  const track = (work: Promise<void>): Promise<void> => {
    settling = Promise.all([settling, work]).then(() => undefined, () => undefined);
    return work;
  };
  const stateWatchers = new Set<(state: VisitState) => void>();
  const visitorWatchers = new Set<(visitor: ShareVisitor) => void>();

  function set(next: VisitState): void {
    if (closed) return;
    state = next;
    for (const fn of [...stateWatchers]) fn(next);
  }

  function heard(next: ResolvedShare): void {
    resolved = next;
    visitor = next.visitor;
    for (const fn of [...visitorWatchers]) fn(next.visitor);
  }

  async function openScope(next: SharedScope): Promise<SharedStore | undefined> {
    try {
      const store = await deps.open(next);
      if (closed) {
        await store.close();
        return undefined;
      }
      return store;
    } catch {
      set({ kind: "nostorage" });
      return undefined;
    }
  }

  /**
   * Opens or moves to the scope the server says this visitor reads. Moved,
   * not reopened: the screen stays, and hears only what differs.
   *
   * One move at a time. A second answer can name the same scope while the
   * first move is still syncing -- a hold on the old copy asks the server
   * again -- and two stores on one database would each send its queue.
   */
  let moving: Promise<void> = Promise.resolve();
  function adopt(next: SharedScope): Promise<void> {
    const work = moving.then(() => move(next));
    moving = work.catch(() => undefined);
    return work;
  }

  async function move(next: SharedScope): Promise<void> {
    if (page && scope && scopeDatabaseName(scope) === scopeDatabaseName(next)) return;
    const store = await openScope(next);
    if (!store) return;
    if (!page) {
      scope = next;
      page = switchableStore(store);
      set({ kind: "open", store: page });
      return;
    }
    // Its first answer before the switch, so the page never shows the new
    // scope empty.
    await store.sync().catch(() => undefined);
    if (closed) {
      await store.close();
      return;
    }
    scope = next;
    await page.switchTo(store);
  }

  function resolve(): Promise<void> {
    return track(ask());
  }

  async function ask(): Promise<void> {
    const mine = ++asked;
    try {
      const answer = await deps.resolve(credential);
      if (mine !== asked || closed) return;
      heard(answer);
      await adopt(sharedScope(credential, answer, account()));
    } catch (failure: unknown) {
      if (mine !== asked || closed) return;
      // With a copy open, its own sync says what happened: an ended link ends
      // the scope and marks what was unsent. Only a page with nothing yet
      // needs one of these.
      if (page) return;
      const code = failure instanceof ApiError ? failure : undefined;
      if (code?.status === 429) set({ kind: "limited" });
      else if (code?.status === 401 && credential.kind === "added") set({ kind: "signin" });
      else if (code && code.status !== 0) set({ kind: "unavailable" });
      else set({ kind: "unreachable" });
    }
  }

  function begin(): void {
    // A copy from an earlier visit opens at once, offline included, and the
    // server is asked in the background.
    const remembered = rememberedSharedScope(credential, account());
    if (remembered) {
      void track(adopt(remembered)).then(() => resolve());
      return;
    }
    set({ kind: "loading" });
    void resolve();
  }

  begin();

  return {
    state: () => state,
    onState(fn) {
      stateWatchers.add(fn);
      return () => { stateWatchers.delete(fn); };
    },
    visitor: () => visitor ?? (page?.share().visitor ?? undefined),
    listed: () => (addedNow ? "added" : page?.share().listed ?? resolved?.listed),
    onVisitor(fn) {
      visitorWatchers.add(fn);
      return () => { visitorWatchers.delete(fn); };
    },
    scope: () => scope,
    retry() {
      if (page) void page.sync();
      else begin();
    },
    async name(displayName) {
      await settling;
      const who = visitor ?? page?.share().visitor ?? undefined;
      if (who?.kind === "account") {
        const user = await deps.nameAccount(displayName);
        if (resolved) heard({ ...resolved, visitor: { ...who, name: user.name ?? displayName, needsName: false } });
        await page?.sync();
        return;
      }
      // An added list is opened only by its account, so only a link names a guest.
      if (credential.kind !== "link") throw new Error("A guest is named only through a link.");
      const named = await deps.nameGuest(credential.token, displayName);
      if (!resolved) {
        // Named before the first answer came: ask again for the list's facts.
        await resolve();
        return;
      }
      heard({ ...resolved, visitor: named });
      await track(adopt(sharedScope(credential, { ...resolved, visitor: named }, account())));
      await page?.sync();
    },
    refresh: resolve,
    async add() {
      // Only a link adds: a list opened from the visitor's Lists is in them already.
      if (credential.kind !== "link") return;
      await deps.add(credential.token);
      addedNow = true;
    },
    async holdAdd() {
      if (credential.kind !== "link") return;
      await deps.holdAdd(credential.token);
    },
    async close() {
      closed = true;
      stateWatchers.clear();
      visitorWatchers.clear();
      await page?.close();
    },
  };
}
